/**
 * Punto de entrada del módulo radio.
 *
 * `initRadio()` es lo que llama el frontend (desde main.ts) para activar
 * el módulo. Hace todo lo que el legacy hacía en su `init()`:
 *  1. Carga playlists y hotspots desde localStorage.
 *  2. Monta el modal en el DOM.
 *  3. Aplica las posiciones de los hotspots.
 *  4. Renderiza todo.
 *  5. Conecta los listeners de los botones, knob, progreso, file picker,
 *     modal de nueva playlist.
 *  6. Conecta los listeners globales (ESC para cerrar, espacio para play,
 *     click en hotspot /radio de la sala para abrir).
 *
 * Idempotente: llamar varias veces no duplica listeners.
 */

import { me } from '../auth/api.js';
import { loadHotspots, loadPlaylists } from './persistence.js';
import { mountRadio, openRadio, closeRadio } from './mount.js';
import { renderAll } from './render.js';
import { applyAllHotspots } from './hotspots.js';
import { setHotspots, setPlaylists, setUserTracks, state } from './state.js';
import {
  getCallbacks,
  pauseAudio,
  saveCurrentPlaylist,
  startScrub,
  stopAudio,
  togglePlay,
  updatePlayState,
  flashHotspot,
} from './controls.js';
import { idbGetAllTracks } from './idb-store.js';
import { bindAudioListeners, initKnob, initProgress } from './knob-progress.js';
import { initFolderPicker, reconcileGhosts } from './folder-picker.js';
import { loadServerMusic } from './server-music.js';
import { closeNewPlaylistModal, initNewPlaylistModal } from './new-playlist.js';
import { initRadioEditor, toggleRadioEditMode } from './editor.js';
import type { Track } from './types.js';

let initialized = false;

/**
 * ¿Está disponible el editor de hotspots de la radio en esta sesión?
 * Lo decide `gateRadioEditor()` durante el arranque: true en dev, y en
 * producción sólo si el usuario es superadmin. Mientras sea false, el
 * botón [E] EDITAR queda oculto y la tecla E del modal no hace nada.
 */
let radioEditorAllowed = false;

/**
 * Inicializa el módulo radio. Idempotente.
 *
 * Llamada típica: desde `main.ts` justo después de cargar los módulos
 * de la escena. Es independiente de la sala — la radio puede vivir sola.
 */
export function initRadio(): void {
  if (initialized) return;
  initialized = true;

  // 1. Estado desde localStorage
  setPlaylists(loadPlaylists());
  setHotspots(loadHotspots());

  // 2. Montar modal en el DOM
  mountRadio();

  // 3. Posiciones iniciales de los hotspots
  applyAllHotspots();

  // 4. Render inicial
  renderAll(getCallbacks());
  updatePlayState();

  // 5. Listeners de botones del player
  bindButton('r-hs-play', togglePlay);
  bindButton('r-hs-stop', stopAudio);
  bindButton('r-hs-pause', pauseAudio);
  bindButton('r-hs-rew', () => startScrub('rew'));
  bindButton('r-hs-ff', () => startScrub('ff'));
  bindButton('r-hs-rec', () => {
    if (state.editMode) return;
    flashHotspot('r-hs-rec');
    // REC sin comportamiento definido todavía. Solo flash visual.
  });

  // Botón SAVE
  bindButton('r-save-btn', saveCurrentPlaylist);

  // Botón CERRAR del header
  bindButton('r-close-btn', () => {
    if (state.editMode) toggleRadioEditMode();
    closeRadio();
  });

  // Botón EDITAR del header — abre/cierra el modo edición de hotspots
  bindButton('r-edit-btn', toggleRadioEditMode);

  // 6. Subsistemas
  initKnob();
  initProgress();
  bindAudioListeners();
  initFolderPicker();
  initNewPlaylistModal();
  initRadioEditor();

  // 7. Listeners globales: ESC, click fuera, hotspot /radio
  bindGlobalListeners();

  // 8. Cargas asíncronas (no bloquean el render inicial):
  //    - Música del servidor → rellena la default playlist del escuadrón.
  //    - Tracks del usuario persistidas en IndexedDB → rellenan los ghosts
  //      de las playlists del usuario.
  //    - Gate del editor de hotspots → revela el botón [E] EDITAR si procede.
  void loadServerMusic();
  void loadPersistedTracks();
  void gateRadioEditor();
}

/**
 * Lee los blobs guardados en IndexedDB y los promueve a `state.userTracks`,
 * generando blob URLs frescas para esta sesión. Ejecuta `reconcileGhosts()`
 * para que las playlists guardadas con ids ghost se re-asocien a los
 * archivos reales recién cargados, y refresca la UI.
 *
 * Es no-bloqueante: el modal aparece inmediatamente con la última
 * selección guardada en localStorage como ghosts; al terminar este
 * fetch (típicamente <100ms con cientos de canciones), los ghosts se
 * convierten en reales y la UI se actualiza.
 */
async function loadPersistedTracks(): Promise<void> {
  const records = await idbGetAllTracks();
  if (records.length === 0) return;
  const tracks: Track[] = records.map((r) => ({
    id: r.id,
    name: r.name,
    file: URL.createObjectURL(r.blob),
    isUser: true,
  }));
  setUserTracks(tracks);
  reconcileGhosts();
  renderAll(getCallbacks());
}

/**
 * Conecta un listener de click a un botón con guard contra duplicación
 * vía dataset. Si el elemento no existe, silencioso.
 */
function bindButton(elId: string, handler: () => void): void {
  const el = document.getElementById(elId);
  if (!el) return;
  if (el.dataset.bound === '1') return;
  el.dataset.bound = '1';
  el.addEventListener('click', handler);
}

let globalsBound = false;

/** Listeners que viven en `document` o en el overlay. Una sola vez. */
function bindGlobalListeners(): void {
  if (globalsBound) return;
  globalsBound = true;

  // Click fuera del modal (en el overlay) → cerrar
  const overlay = document.getElementById('r-overlay');
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && !state.editMode) closeRadio();
    });
  }

  // Tecla: ESC cierra el modal de nueva playlist o el modal grande;
  // ESPACIO toggles play.
  document.addEventListener('keydown', (e) => {
    if (!state.isOpen) return;
    const target = e.target as HTMLElement | null;
    const tag = (target?.tagName ?? '').toLowerCase();
    const isInput = tag === 'input' || tag === 'textarea';

    if (isInput) {
      // Sólo ESC cuando hay input activo, para cerrar el modal de nueva
      if (e.key === 'Escape') {
        const newPlOpen = document
          .getElementById('r-newpl-modal')
          ?.classList.contains('r-show');
        if (newPlOpen) {
          closeNewPlaylistModal();
          e.preventDefault();
        }
      }
      return;
    }

    if (e.key === 'Escape') {
      const newPlOpen = document
        .getElementById('r-newpl-modal')
        ?.classList.contains('r-show');
      if (newPlOpen) {
        closeNewPlaylistModal();
      } else if (state.editMode) {
        // Si estamos editando, ESC sale del editor (no cierra el modal)
        toggleRadioEditMode();
      } else {
        closeRadio();
      }
    } else if ((e.key === 'e' || e.key === 'E') && state.isOpen && radioEditorAllowed) {
      // Tecla E dentro del modal: toggle del editor (sólo si está permitido)
      e.preventDefault();
      toggleRadioEditMode();
    } else if (e.key === ' ' && !state.editMode) {
      e.preventDefault();
      togglePlay();
    }
  });

  // Interceptar el hotspot /radio de la sala de operaciones
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const link = target?.closest('a[href="/radio"]');
    if (link) {
      e.preventDefault();
      openRadio();
    }
  });
}

/**
 * Decide si el editor de hotspots de la radio está disponible en esta
 * sesión y, si lo está, lo habilita: levanta el flag `radioEditorAllowed`
 * (que activa la tecla E) y revela el botón [E] EDITAR del header.
 *
 *  - En dev (`npm run dev`, sólo en local): siempre disponible.
 *  - En producción: sólo para el superadmin (chequeo vía GET /api/auth/me).
 *
 * Si no procede, no hace nada: el botón sigue con `hidden` y la tecla E
 * del modal queda inerte. Se invoca con `void` desde initRadio.
 */
async function gateRadioEditor(): Promise<void> {
  if (!import.meta.env.DEV) {
    const result = await me();
    const isSuperadmin = result.ok && result.data.user.role === 'superadmin';
    if (!isSuperadmin) return;
  }
  radioEditorAllowed = true;
  document.getElementById('r-edit-btn')?.removeAttribute('hidden');
}
